import { useEffect, useState, useMemo, useCallback } from 'react';
import executionTracker from '@kemu-io/kemu-core/common/executionTracker';
import { getGroupInnerWidgets } from '@src/app/recipe/utils';

/**
 * Hook to track if any child widgets inside a widget group are currently processing.
 * 
 * Returns true if any child widget (including widgets in nested groups) is currently
 * processing data, false otherwise. This allows widget groups to display loading
 * animations when their children are processing, even if the group itself isn't
 * directly marked as processing.
 */
const useChildWidgetsProcessingState = (config: {
  recipeId: string;
  thingId: string;
  widgetId: string;
  isWidgetGroup: boolean;
}): boolean => {
  const { recipeId, thingId, widgetId, isWidgetGroup } = config;
  const [hasChildProcessing, setHasChildProcessing] = useState(false);

  // Get child widgets if this is a widget group - memoize to avoid unnecessary re-renders
  const childWidgets = useMemo(() => {
    if (!isWidgetGroup) { return []; }
    return getGroupInnerWidgets(recipeId, thingId, widgetId);
  }, [isWidgetGroup, recipeId, thingId, widgetId]);

  // Check if any child widgets are currently processing
  const checkChildWidgetsState = useCallback(() => {
    const anyProcessing = childWidgets.some((childWidget) => {
      const state = executionTracker.getExecutionState({
        thingId: thingId,
        widgetId: childWidget.id,
      });
      return state.isProcessing;
    });

    setHasChildProcessing(anyProcessing);
  }, [childWidgets, thingId]);

  // Subscribe to global processing state changes
  useEffect(() => {
    if (!isWidgetGroup || childWidgets.length === 0) {
      setHasChildProcessing(false);
      return;
    }

    // Check initial state
    checkChildWidgetsState();

    // Poll periodically when global processing is active to catch intermediate state changes
    // The global event only fires when global state toggles (first widget starts/last widget finishes),
    // not for every individual widget change, so we need to poll when processing is active
    let timeoutId: NodeJS.Timeout | null = null;
    
    const stopPolling = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const scheduleNextCheck = () => {
      if (timeoutId) return; // Already scheduled
      timeoutId = setTimeout(() => {
        timeoutId = null; // Clear the timeout ID before checking
        const globalState = executionTracker.getLastGlobalProcessingState();
        if (globalState?.isProcessing) {
          checkChildWidgetsState();
          scheduleNextCheck(); // Schedule next check only if still processing
        }
      }, 200);
    };

    // Subscribe to global processing state changes to start/stop polling
    const unsubscribe = executionTracker.onGlobalProcessingStateChange((event) => {
      checkChildWidgetsState();
      if (event.isProcessing) {
        scheduleNextCheck();
      } else {
        stopPolling();
      }
    });

    // Start polling if processing is already active (e.g., component mounts during processing)
    const globalState = executionTracker.getLastGlobalProcessingState();
    if (globalState?.isProcessing) {
      scheduleNextCheck();
    }

    return () => {
      unsubscribe();
      stopPolling();
    };
  }, [isWidgetGroup, childWidgets, checkChildWidgetsState]);

  return hasChildProcessing;
};

export default useChildWidgetsProcessingState;
